import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { Heading } from '@mm/components';
import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import { useViewActions } from '@mm/state/actions/viewActions';
import ConnectionModel, {
  IntegrationTypes,
} from '@mm/state/model/ConnectionModel';
import WidgetModel from '@mm/state/model/WidgetModel';
import { selectConnectionWidgetsByType } from '@mm/state/modules/connectionsTableState/selectors';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { Switch } from 'antd';
import classnames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { OnClickElsewhere } from '../../../../../common/components/OnClickElsewhere/OnClickElsewhere';
import TextInput from '../../../../../common/components/TextInput';
import { useCommonWidgetHandlers } from '../../../../../common/helpers/widget-helpers/use-common-widget-handlers';
import { Menu } from '../../../../features/beta/modules/Automations/AutomationEdit/DialogueEditor/NodeTypes/elements/NodeMenu/NodeMenu';
import { useIntegrationWidget } from '../../../../features/beta/modules/Automations/AutomationEdit/DialogueEditor/NodeTypes/Integration/use-integration-widget';
import { humanizedIntegrationNames } from '../../../../features/beta/modules/Dashboard/ActivationBlock/use-activation-steps';
import { GoHighLevelIntegration } from '../../../../features/beta/modules/Integrations/NativeIntegrations/components/GoHighLevelIntegrations/GoHighLevelIntegrations';
import { KlaviyoIntegrations } from '../../../../features/beta/modules/Integrations/NativeIntegrations/components/KlaviyoIntegrations/KlaviyoIntegrations';
import { SalesForceIntegrations } from '../../../../features/beta/modules/Integrations/NativeIntegrations/components/SalesForceIntegrations/SalesForceIntegrations';
import { SendlaneIntegrations } from '../../../../features/beta/modules/Integrations/NativeIntegrations/components/SendlaneIntegrations/SendlaneIntegrations';
import { ShopifyIntegrations } from '../../../../features/beta/modules/Integrations/NativeIntegrations/components/ShopifyIntegrations/ShopifyIntegrations';
import { SlideoutMenu } from '../../components/SlideoutMenu';
import { useSlideoutOnClose } from '../SlideoutEmail/SlideoutEmail';
import styles from './IntegrationWidgetSlideoutEditor.module.css';
import { WidgetConnectionLists } from './WidgetConnectionLists/WidgetConnectionLists';
import { WidgetConnectionTags } from './WidgetConnectionTags/WidgetConnectionTags';

export type IntegrationWidgetSlideoutEditorProps = {
  connectionId: number;
  widget: WidgetModel;
  type: IntegrationTypes;
};

export const IntegrationWidgetSlideoutEditor = ({
  connectionId,
  widget,
  type,
}: IntegrationWidgetSlideoutEditorProps) => {
  const activeFbPageId = useSelector(activeFacebookPageIdSelector);
  const widgetConnections: ConnectionModel[] = useSelector(
    (state: StoreState) => selectConnectionWidgetsByType(state, type),
  );
  const connection = widgetConnections?.find(c => c.id === connectionId);

  const { retrieveById, update } = useConnectionCRUDActions();
  const { setRef } = useSlideoutOnClose();
  const viewActions = useViewActions();
  const commonWidgetHandlers = useCommonWidgetHandlers();
  const { openDeleteConfirmModal } = useModalActions();
  const { getIntegrationWidgetConfig, createOrUpdateConnection } =
    useIntegrationWidget(type, widget);
  const { accounts, getTokenId, authParams, getLists, lists, tagsDisabled } =
    getIntegrationWidgetConfig(type) || {};
  const hasAccounts = !!accounts?.length;

  const humanizedIntegrationName = humanizedIntegrationNames[type];

  const [nameEditMode, setNameEditMode] = useState(false);
  const [localName, setLocalName] = useState(connection?.name || '');

  const component = (type: IntegrationTypes) => {
    switch (type) {
      case 'salesforce':
        return (
          <SalesForceIntegrations version="widget" connection={connection} />
        );
      case 'gohighlevel':
        return (
          <GoHighLevelIntegration version="widget" connection={connection} />
        );
      case 'shopify':
        return <ShopifyIntegrations version="widget" connection={connection} />;
      case 'klaviyo':
        return <KlaviyoIntegrations connection={connection} version="widget" />;
      case 'sendlane':
        return (
          <SendlaneIntegrations connection={connection} version="widget" />
        );
      default:
        return null;
    }
  };

  const fetchConnection = async () => {
    if (connectionId) {
      await retrieveById(activeFbPageId, connectionId);
    }
  };

  const onClickSaveName = () => {
    update(activeFbPageId, { ...connection, name: localName || '' });
    setNameEditMode(false);
  };

  const onClickCancelSaveName = () => {
    setNameEditMode(false);
  };

  const tokenId = getTokenId?.(connection);

  const fetchLists = (connection?: ConnectionModel) => {
    const tokenId = getTokenId?.(connection);
    const klaviyoTokenType = connection?.klaviyoAccessToken
      ? 'api_key'
      : connection?.klaviyoOauthToken
      ? 'oauth_token'
      : null;

    if (getLists && tokenId) {
      if (type === 'klaviyo' && klaviyoTokenType) {
        (
          getLists as (
            pageId: number,
            tokenId: number,
            tokenType: 'api_key' | 'oauth_token',
          ) => void
        )(activeFbPageId, tokenId, klaviyoTokenType);
      } else if (type !== 'klaviyo') {
        (getLists as (pageId: number, tokenId: number, force: boolean) => void)(
          activeFbPageId,
          tokenId,
          true,
        );
      }
    }
  };

  const onClickDelete = () => {
    const deleteCallback = async () => {
      if (widget) {
        viewActions.hideSlideoutMenu();
        await commonWidgetHandlers.delete(widget);
      }
    };

    const headerText = `Are you sure you want to delete this ${humanizedIntegrationName} widget?`;

    openDeleteConfirmModal({
      deleteCallback,
      headerText,
      newStyle: true,
    });
  };

  const actionsMenu = [
    {
      disabled: false,
      children: (
        <span className={styles.menuItem}>
          <DeleteOutlined /> Delete
        </span>
      ),
      onClick: onClickDelete,
    },
  ];

  useEffect(() => {
    fetchLists(connection);
  }, [connection?.id]);

  useEffect(() => {
    if (hasAccounts && authParams && !tokenId && connection) {
      createOrUpdateConnection(connection, authParams, fetchLists);
    }
  }, [hasAccounts]);

  if (!connection) return null;

  return (
    <SlideoutMenu className={styles.menu} setRef={setRef}>
      <div className={styles.headerContent}>
        {nameEditMode ? (
          <OnClickElsewhere handleClick={onClickCancelSaveName}>
            <div className={styles.header}>
              <TextInput
                value={localName}
                onChange={e => setLocalName(e.target.value)}
                disabled={!nameEditMode}
                placeholder="Connection Name"
                maxLength={50}
                className={classnames(styles.nameInput, {
                  [styles.nameInputDisabledMode]: !nameEditMode,
                })}
              />

              <div onClick={onClickSaveName} className={styles.saveBtn}>
                <CheckOutlined className={styles.checkmark} />
              </div>
            </div>
          </OnClickElsewhere>
        ) : (
          <div className={styles.header}>
            <Heading className={styles.headerTitle} size={3}>
              {connection?.name || `${humanizedIntegrationName} Integration`}
            </Heading>
            <EditOutlined
              onClick={() => setNameEditMode(true)}
              className={styles.editIcon}
            />
          </div>
        )}

        {!nameEditMode && (
          <div className={styles.headerActions}>
            <Switch
              defaultChecked={connection?.active}
              checkedChildren="Active"
              unCheckedChildren="Pause"
              className={styles.switch}
              onChange={ok =>
                createOrUpdateConnection(connection, { active: ok })
              }
              disabled={!connection?.id}
            />
            <Menu
              menu={actionsMenu}
              direction="bottom"
              useThreeDotButton
              className={styles.actionMenu}
            />
            <CloseOutlined
              className={styles.closeIcon}
              onClick={viewActions.hideSlideoutMenu}
            />
          </div>
        )}
      </div>

      <SlideoutMenu.Content className={styles.content}>
        {component(type)}
        {tokenId && (
          <>
            {' '}
            {getLists && (
              <WidgetConnectionLists
                defaultValue={connection?.data?.listId}
                lists={lists}
                onChange={listId =>
                  createOrUpdateConnection(connection, {
                    data: { ...(connection?.data || {}), listId },
                  })
                }
              />
            )}
            {!tagsDisabled && (
              <WidgetConnectionTags
                connection={connection}
                widgetName={humanizedIntegrationName}
                type={type}
              />
            )}
          </>
        )}
      </SlideoutMenu.Content>
    </SlideoutMenu>
  );
};
